import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_STRING as api } from '@/App';
import { useDarkMode } from '@/hooks/useDarkMode';

import { CheckCircle, Clock, Mail, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const darkMode = useDarkMode();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error', 'already_verified'
  const [message, setMessage] = useState('');
  const verifyStarted = useRef(false);

  const verifyEmail = useCallback(async () => {
    try {
      const response = await fetch(`${api}/auth/verify-email?token=${token}`);
      const data = await response.json();

      if (response.ok) {
        if (data.already_verified) {
          setStatus('already_verified');
          setMessage('Your email is already verified!');
        } else {
          setStatus('success');
          setMessage('Email verified successfully!');
        }
      } else {
        setStatus('error');
        setMessage(data.detail || 'Verification failed');
      }
    } catch (error) {
      setStatus('error');
      setMessage('An error occurred during verification');
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('No verification token provided');
      return;
    }

    // Guard against React StrictMode double-invoking this effect in dev:
    // the token is single-flight, so only the first run should call the API.
    if (verifyStarted.current) return;
    verifyStarted.current = true;

    verifyEmail();
  }, [token, verifyEmail]);

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 transition-colors"
      style={{ background: darkMode.bgPrimary }}
    >
      <div 
        className="max-w-md w-full rounded-2xl shadow-2xl p-8 text-center transition-colors"
        style={{
          background: darkMode.isDark ? '#2D4354' : 'white',
          border: darkMode.isDark ? '2px solid #FED7A5' : 'none'
        }}
      >
        {status === 'verifying' && (
          <>
            <div className="mb-6 flex justify-center">
              <Clock className="w-20 h-20 animate-spin" style={{ color: '#FED7A5' }} />
            </div>
            <h1 
              className="text-2xl font-bold mb-2 transition-colors"
              style={{ fontFamily: 'Playfair Display, serif', color: darkMode.textPrimary }}
            >
              Verifying Your Email
            </h1>
            <p className="transition-colors" style={{ color: darkMode.textSecondary }}>
              Please wait while we verify your email address...
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="mb-6 flex justify-center">
              <CheckCircle className="w-20 h-20" style={{ color: '#22C55E' }} />
            </div>
            <h1 
              className="text-2xl font-bold mb-2 transition-colors"
              style={{ fontFamily: 'Playfair Display, serif', color: darkMode.textPrimary }}
            >
              Email Verified! 🎉
            </h1>
            <p className="mb-6 transition-colors" style={{ color: darkMode.textSecondary }}>
              {message}
            </p>
            <Button
              onClick={() => window.location.assign('/')}
              className="w-full"
              style={{ background: '#9E6752', color: 'white' }}
              data-testid="verify-success-home-btn"
            >
              Go to Home
            </Button>
          </>
        )}

        {status === 'already_verified' && (
          <>
            <div className="mb-6 flex justify-center">
              <Mail className="w-20 h-20" style={{ color: '#FED7A5' }} />
            </div>
            <h1 
              className="text-2xl font-bold mb-2 transition-colors"
              style={{ fontFamily: 'Playfair Display, serif', color: darkMode.textPrimary }}
            >
              Already Verified
            </h1>
            <p className="mb-6 transition-colors" style={{ color: darkMode.textSecondary }}>
              {message}
            </p>
            <Button
              onClick={() => navigate('/')}
              className="w-full"
              style={{ background: '#9E6752', color: 'white' }}
            >
              Go to Home
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="mb-6 flex justify-center">
              <XCircle className="w-20 h-20" style={{ color: '#EF4444' }} />
            </div>
            <h1 
              className="text-2xl font-bold mb-2 transition-colors"
              style={{ fontFamily: 'Playfair Display, serif', color: darkMode.textPrimary }}
            >
              Verification Failed
            </h1>
            <p className="mb-6 transition-colors" style={{ color: darkMode.textSecondary }}>
              {message}
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/')}
                className="w-full"
                style={{ background: '#9E6752', color: 'white' }}
              >
                Go to Home
              </Button>
              <p className="text-sm transition-colors" style={{ color: darkMode.textSecondary }}>
                Need help? Contact us at support@mementa.gifts
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
